import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    vendorName: '',
    contactInfo: '',
    leadTimeDays: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadVendors();
    } else {
      setError('Please login to view vendors');
      setLoading(false);
    }
  }, [tenantId]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getVendors(tenantId);
      setVendors(data || []);
    } catch (err) {
      setError('Failed to load vendors. Please try again.');
      console.error('Error loading vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contactInfo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingVendor(null);
    setFormData({
      vendorName: '',
      contactInfo: '',
      leadTimeDays: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendorName: vendor.vendorName || '',
      contactInfo: vendor.contactInfo || '',
      leadTimeDays: vendor.leadTimeDays?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for vendors.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete vendor. Please try again.');
      console.error('Error deleting vendor:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.vendorName.trim()) {
      errors.vendorName = 'Vendor name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const vendorData = {
        tenantId: tenantId,
        vendorName: formData.vendorName.trim(),
        contactInfo: formData.contactInfo.trim() || null,
        leadTimeDays: formData.leadTimeDays ? parseInt(formData.leadTimeDays) : null,
      };

      if (editingVendor) {
        vendorData.vendorId = editingVendor.vendorId;
        await masterDataService.updateVendor(editingVendor.vendorId, vendorData);
      } else {
        await masterDataService.createVendor(vendorData);
      }

      await loadVendors();
      setShowModal(false);
      setFormData({ vendorName: '', contactInfo: '', leadTimeDays: '' });
      setEditingVendor(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save vendor. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setFormData({ vendorName: '', contactInfo: '', leadTimeDays: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading vendors..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Vendors</h1>
          <p>Manage vendors for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Vendor
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredVendors.length} {filteredVendors.length === 1 ? 'vendor' : 'vendors'}
          </div>
        </div>

        {filteredVendors.length === 0 ? (
          <div className="empty-state">
            <HiOutlineBuildingOffice2 className="empty-icon" />
            <h3>No vendors found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first vendor'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Vendor
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Vendor Name</th>
                <th>Contact Info</th>
                <th>Lead Time (days)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor) => (
                <tr key={vendor.vendorId}>
                  <td>{vendor.vendorId}</td>
                  <td className="name-cell">{vendor.vendorName}</td>
                  <td>{vendor.contactInfo || '-'}</td>
                  <td>{vendor.leadTimeDays ?? '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(vendor)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="vendor-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingVendor
                  ? 'Updating...'
                  : 'Creating...'
                : editingVendor
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="vendor-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="vendorName">
                Vendor Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="vendorName"
                value={formData.vendorName}
                onChange={(e) =>
                  setFormData({ ...formData, vendorName: e.target.value })
                }
                className={formErrors.vendorName ? 'error' : ''}
                placeholder="Enter vendor name"
              />
              {formErrors.vendorName && (
                <span className="field-error">{formErrors.vendorName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="contactInfo">Contact Info</label>
              <textarea
                id="contactInfo"
                value={formData.contactInfo}
                onChange={(e) =>
                  setFormData({ ...formData, contactInfo: e.target.value })
                }
                placeholder="Enter contact information (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="leadTimeDays">Lead Time (days)</label>
              <input
                type="number"
                id="leadTimeDays"
                value={formData.leadTimeDays}
                onChange={(e) =>
                  setFormData({ ...formData, leadTimeDays: e.target.value })
                }
                placeholder="Enter lead time in days (optional)"
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default VendorsPage;
